import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../services/apiService';
import { getCurrentUser } from 'aws-amplify/auth';

function ImageManagement() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [newFileName, setNewFileName] = useState('');
  const fileInputRef = useRef(null);
  useEffect(() => {
    checkAdminStatus();
    fetchImages();
  }, []);

  const checkAdminStatus = async () => {
    try {
      getCurrentUser()
        .then(user => setUser(user))
        .catch(() => setUser(null)); // not signed in
    } catch (error) {
      console.error('Error checking admin status:', error);
      setUser(false);
    }
  };

  const fetchImages = async () => {
    try {
      const data = await apiService.getImages();
      setImages(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching images:', err);
      setError('Failed to load images. Please try again.');
      setLoading(false);
    }
  };

  const handleDelete = async (imageName) => {
    if (window.confirm(`Are you sure you want to delete ${imageName}?`)) {
      try {
        await apiService.deleteImage(imageName);
        // Refresh the image list
        fetchImages();
      } catch (err) {
        console.error('Error deleting image:', err);
        setError('Failed to delete image. Please try again.');
      }
    }
  };

  const [selectedFile, setSelectedFile] = useState(null);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    setError(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      await apiService.uploadImage(selectedFile, newFileName.trim() || null);
      setNewFileName('');
      setSelectedFile(null);
      fileInputRef.current.value = '';
      fetchImages();
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(err.message || 'Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  if (!user) {
    return <div className="container mt-5">
      <h2>Access Denied</h2>
      <p>You must be an administrator to access this page.</p>
    </div>;
  }

  return (
    <div className="container mt-5">
      <h2>Image Management</h2>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="mb-4 card p-4">
        <h4>Upload New Image</h4>
        <p>Upload a JPG/JPEG file. The image will be automatically processed and available in multiple sizes.</p>
        <p>Required image dimensions: Minimum width 600px, minimum height 420px</p>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label htmlFor="newFileName" className="form-label">New File Name (optional)</label>
            <input
              type="text"
              className="form-control"
              id="newFileName"
              value={newFileName}
              onChange={(e) => setNewFileName(e.target.value)}
              placeholder="Enter new file name (without extension)"
              disabled={uploading}
            />
          </div>

          <div className="mb-3">
            <label htmlFor="imageFile" className="form-label">Select Image</label>
            <input
              type="file"
              className="form-control"
              id="imageFile"
              ref={fileInputRef}
              accept=".jpg,.jpeg"
              onChange={handleFileChange}
              disabled={uploading}
            />
          </div>

          <div className="mb-3">
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={uploading || !selectedFile}
            >
              {uploading ? 'Uploading...' : 'Upload Image'}
            </button>
          </div>

          {uploading && (
            <div className="text-center mt-3">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Uploading...</span>
              </div>
            </div>
          )}
        </form>
      </div>

      {loading ? (
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="row">
          {images.map((image) => (
            <div key={image.name} className="col-md-4 mb-4">
              <div className="card">
                <img
                  src={`/${image.original}`}
                  className="card-img-top"
                  alt={image.name}
                  style={{ height: '200px', objectFit: 'cover' }}
                />
                <div className="card-body">
                  <h5 className="card-title">{image.name}</h5>
                  <p className="card-text">
                    Available sizes:
                    <ul>
                      {image.variants.map((variant) => (
                        <li key={variant.dimensions}>
                          {variant.dimensions} - /{variant.path}
                        </li>
                      ))}
                    </ul>
                  </p>
                  <button
                    className="btn btn-danger"
                    onClick={() => handleDelete(image.name)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ImageManagement;